import { useEffect, useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Pencil, Plus, TrendingUp } from 'lucide-react';
import {
  useCreateFuelMerchant,
  useFuelGrades,
  useFuelMerchants,
  usePriceTrend,
  usePriceZones,
  useUpdateFuelMerchant,
} from '@/api/hooks/useFuelMerchants';
import type {
  FuelMerchantCreate,
  FuelMerchantRow,
  FuelMerchantUpdate,
} from '@/types/merchants';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const RPL = new Intl.NumberFormat('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const NO_ZONE = 'none';
const RANGE_OPTIONS: { label: string; months: number }[] = [
  { label: '6M', months: 6 },
  { label: '12M', months: 12 },
  { label: '24M', months: 24 },
  { label: '5Y', months: 60 },
  { label: 'LTD', months: 240 },
];

function money(v: number | null | undefined): string {
  return v == null ? '—' : `R ${RPL.format(v)}`;
}

// ===========================================================================
// Page
// ===========================================================================

export default function FuelMerchants() {
  const zonesQ = usePriceZones();
  const gradesQ = useFuelGrades();

  const [zoneId, setZoneId] = useState<number | undefined>(undefined);
  const [grade, setGrade] = useState<string>('ULP_95');
  const [months, setMonths] = useState<number>(24);

  const [search, setSearch] = useState('');
  const merchantsQ = useFuelMerchants({ q: search });

  const trendQ = usePriceTrend({ zoneId, grade, months });

  // Default the zone / grade selectors once the reference lists arrive.
  useEffect(() => {
    if (zoneId == null && zonesQ.data && zonesQ.data.length > 0) {
      setZoneId(zonesQ.data[0].price_zone_id);
    }
  }, [zonesQ.data, zoneId]);

  useEffect(() => {
    if (gradesQ.data && gradesQ.data.length > 0) {
      const hasUlp95 = gradesQ.data.some((g) => g.description === 'ULP_95');
      if (!hasUlp95) setGrade(gradesQ.data[0].description);
    }
  }, [gradesQ.data]);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<FuelMerchantRow | null>(null);

  const openCreate = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (m: FuelMerchantRow) => {
    setEditing(m);
    setDialogOpen(true);
  };

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Fuel Merchants</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Manage fuel merchants and their pricing-zone link, and track how the
            regulated fuel price moved over time.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          New merchant
        </Button>
      </div>

      <PriceTrendSection
        zones={zonesQ.data ?? []}
        grades={gradesQ.data ?? []}
        zoneId={zoneId}
        grade={grade}
        months={months}
        onZoneChange={setZoneId}
        onGradeChange={setGrade}
        onMonthsChange={setMonths}
        trend={trendQ}
      />

      <MerchantListSection
        search={search}
        onSearch={setSearch}
        query={merchantsQ}
        onEdit={openEdit}
      />

      <FuelMerchantDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        zones={zonesQ.data ?? []}
      />
    </div>
  );
}

// ===========================================================================
// Price trend
// ===========================================================================

interface PriceTrendSectionProps {
  zones: { price_zone_id: number; description: string; is_coastal_zone: boolean }[];
  grades: { product_grade_id: number; description: string }[];
  zoneId: number | undefined;
  grade: string;
  months: number;
  onZoneChange: (id: number) => void;
  onGradeChange: (grade: string) => void;
  onMonthsChange: (m: number) => void;
  trend: ReturnType<typeof usePriceTrend>;
}

function PriceTrendSection({
  zones,
  grades,
  zoneId,
  grade,
  months,
  onZoneChange,
  onGradeChange,
  onMonthsChange,
  trend,
}: PriceTrendSectionProps) {
  const stats = trend.data?.stats;
  const zoneName = trend.data?.price_zone_description ?? '—';

  const headline = useMemo(() => {
    if (!stats || stats.points === 0 || stats.start_price == null || stats.latest_price == null) {
      return null;
    }
    const dir = (stats.pct_change ?? 0) >= 0 ? '+' : '';
    return `${grade} in ${zoneName}: ${money(stats.start_price)} → ${money(
      stats.latest_price,
    )}, ${dir}${(stats.pct_change ?? 0).toFixed(1)}% since ${stats.start_date ?? ''}`;
  }, [stats, grade, zoneName]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <TrendingUp className="h-4 w-4 text-primary" />
            Regulated fuel price trend
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Select
              value={zoneId != null ? String(zoneId) : undefined}
              onValueChange={(v) => onZoneChange(Number(v))}
            >
              <SelectTrigger className="h-9 w-[180px]">
                <SelectValue placeholder="Zone" />
              </SelectTrigger>
              <SelectContent>
                {zones.map((z) => (
                  <SelectItem key={z.price_zone_id} value={String(z.price_zone_id)}>
                    {z.description}
                    {z.is_coastal_zone ? ' (coastal)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={grade} onValueChange={onGradeChange}>
              <SelectTrigger className="h-9 w-[130px]">
                <SelectValue placeholder="Grade" />
              </SelectTrigger>
              <SelectContent>
                {grades.map((g) => (
                  <SelectItem key={g.product_grade_id} value={g.description}>
                    {g.description}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex overflow-hidden rounded-md border border-input">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.months}
                  type="button"
                  onClick={() => onMonthsChange(r.months)}
                  className={
                    'px-2.5 py-1.5 text-xs font-medium transition-colors ' +
                    (months === r.months
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-background text-muted-foreground hover:bg-accent')
                  }
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <StatTiles stats={stats} isLoading={trend.isLoading} />

        {headline && (
          <p className="text-sm text-muted-foreground">{headline}</p>
        )}

        <div className="h-64">
          {trend.isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : trend.data && trend.data.series.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={trend.data.series}
                margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
              >
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis
                  dataKey="start_date"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: string) => v.slice(0, 7)}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  width={52}
                  domain={['auto', 'auto']}
                  tickFormatter={(v: number) => `R${v.toFixed(0)}`}
                />
                <Tooltip
                  contentStyle={{
                    background: 'hsl(var(--popover))',
                    border: '1px solid hsl(var(--border))',
                    fontSize: 12,
                  }}
                  formatter={(v: number) => [`R ${RPL.format(v)}`, 'Price / L']}
                  labelFormatter={(v: string) => `Effective ${v}`}
                />
                <Line
                  type="stepAfter"
                  dataKey="price_per_liter"
                  name="R / L"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={{ r: 2 }}
                />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No regulated price history for this zone &amp; grade.
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

interface StatTilesProps {
  stats:
    | {
        start_price: number | null;
        latest_price: number | null;
        pct_change: number | null;
        avg_monthly_change: number | null;
        points: number;
      }
    | undefined;
  isLoading: boolean;
}

function StatTiles({ stats, isLoading }: StatTilesProps) {
  const tiles = [
    { label: 'Start price', value: money(stats?.start_price) },
    { label: 'Latest price', value: money(stats?.latest_price) },
    {
      label: 'Total change',
      value:
        stats?.pct_change == null
          ? '—'
          : `${stats.pct_change >= 0 ? '+' : ''}${stats.pct_change.toFixed(1)}%`,
    },
    { label: 'Avg / month', value: money(stats?.avg_monthly_change) },
  ];
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
      {tiles.map((t) => (
        <div key={t.label} className="rounded-md border bg-card p-3">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {t.label}
          </div>
          {isLoading ? (
            <Skeleton className="mt-1 h-6 w-20" />
          ) : (
            <div className="mt-0.5 text-lg font-semibold tabular-nums">{t.value}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ===========================================================================
// Merchant list
// ===========================================================================

interface MerchantListSectionProps {
  search: string;
  onSearch: (v: string) => void;
  query: ReturnType<typeof useFuelMerchants>;
  onEdit: (m: FuelMerchantRow) => void;
}

function MerchantListSection({ search, onSearch, query, onEdit }: MerchantListSectionProps) {
  const { data, isLoading, error } = query;
  return (
    <Card className="flex-1">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm">
            Fuel merchants{data ? ` (${data.total})` : ''}
          </CardTitle>
          <Input
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search name / code / brand"
            className="h-9 w-64"
          />
        </div>
      </CardHeader>
      <CardContent className="overflow-x-auto px-0">
        {error && (
          <div className="px-6 py-3 text-sm text-destructive">
            Could not load merchants: {(error as Error).message}
          </div>
        )}
        {isLoading ? (
          <div className="space-y-2 px-6 pb-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-7 w-full" />
            ))}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Merchant</TableHead>
                <TableHead>Brand</TableHead>
                <TableHead>Fuel zone</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Edit</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.merchants.length ? (
                data.merchants.map((m) => (
                  <TableRow key={m.merchant_id}>
                    <TableCell>
                      <div className="font-medium">{m.trade_name ?? m.merchant_code}</div>
                      <div className="text-xs text-muted-foreground">{m.merchant_code}</div>
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {m.brand ?? '—'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {m.price_zone_description ? (
                        <span>
                          {m.price_zone_description}
                          {m.is_coastal_zone ? (
                            <span className="text-muted-foreground"> (coastal)</span>
                          ) : null}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not linked</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant={m.is_active ? 'default' : 'secondary'}>
                        {m.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => onEdit(m)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">
                    No fuel merchants found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

// ===========================================================================
// Create / edit dialog
// ===========================================================================

interface FuelMerchantDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editing: FuelMerchantRow | null;
  zones: { price_zone_id: number; description: string; is_coastal_zone: boolean }[];
}

interface FormState {
  merchant_code: string;
  trade_name: string;
  brand: string;
  price_zone_id: string; // NO_ZONE sentinel or a numeric string
  zone_code: string;
  category: string;
  is_active: boolean;
}

const EMPTY_FORM: FormState = {
  merchant_code: '',
  trade_name: '',
  brand: '',
  price_zone_id: NO_ZONE,
  zone_code: '',
  category: 'fuel',
  is_active: true,
};

function FuelMerchantDialog({ open, onOpenChange, editing, zones }: FuelMerchantDialogProps) {
  const createM = useCreateFuelMerchant();
  const updateM = useUpdateFuelMerchant();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const isEdit = editing != null;

  // Reset the form whenever the dialog opens (create) or the target changes (edit).
  useEffect(() => {
    if (!open) return;
    setErrorMsg(null);
    if (editing) {
      setForm({
        merchant_code: editing.merchant_code,
        trade_name: editing.trade_name ?? '',
        brand: editing.brand ?? '',
        price_zone_id: editing.price_zone_id != null ? String(editing.price_zone_id) : NO_ZONE,
        zone_code: editing.zone_code ?? '',
        category: editing.category ?? 'fuel',
        is_active: editing.is_active,
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [open, editing]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const priceZoneId = form.price_zone_id === NO_ZONE ? null : Number(form.price_zone_id);
  const canSubmit = isEdit || form.merchant_code.trim().length > 0;
  const isPending = createM.isPending || updateM.isPending;

  const handleSubmit = async () => {
    setErrorMsg(null);
    try {
      if (isEdit && editing) {
        const patch: FuelMerchantUpdate = {
          trade_name: form.trade_name || null,
          brand: form.brand || null,
          price_zone_id: priceZoneId,
          zone_code: form.zone_code || null,
          category: form.category || null,
          is_active: form.is_active,
        };
        await updateM.mutateAsync({ merchantId: editing.merchant_id, patch });
      } else {
        const payload: FuelMerchantCreate = {
          merchant_code: form.merchant_code.trim(),
          trade_name: form.trade_name || null,
          brand: form.brand || null,
          price_zone_id: priceZoneId,
          zone_code: form.zone_code || null,
          category: form.category || 'fuel',
          is_active: form.is_active,
        };
        await createM.mutateAsync(payload);
      }
      onOpenChange(false);
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: { error?: string } } } })
        ?.response?.data?.detail?.error;
      setErrorMsg(detail ?? (err as Error).message ?? 'Save failed');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit fuel merchant' : 'New fuel merchant'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update this merchant and its fuel-zone link.'
              : 'Create a fuel merchant and link it to its regulated pricing zone.'}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3 py-1">
          <Field label="Merchant code" required>
            <Input
              value={form.merchant_code}
              onChange={(e) => set('merchant_code', e.target.value)}
              disabled={isEdit}
              placeholder="e.g. MERC001"
            />
          </Field>
          <Field label="Trade name">
            <Input
              value={form.trade_name}
              onChange={(e) => set('trade_name', e.target.value)}
              placeholder="e.g. Engen Sandton"
            />
          </Field>
          <Field label="Brand">
            <Input
              value={form.brand}
              onChange={(e) => set('brand', e.target.value)}
              placeholder="e.g. Engen"
            />
          </Field>
          <Field label="Fuel zone">
            <Select
              value={form.price_zone_id}
              onValueChange={(v) => set('price_zone_id', v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a pricing zone" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_ZONE}>Not linked</SelectItem>
                {zones.map((z) => (
                  <SelectItem key={z.price_zone_id} value={String(z.price_zone_id)}>
                    {z.description}
                    {z.is_coastal_zone ? ' (coastal)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Zone code">
              <Input
                value={form.zone_code}
                onChange={(e) => set('zone_code', e.target.value)}
                placeholder="e.g. GP01"
                maxLength={8}
              />
            </Field>
            <Field label="Category">
              <Select value={form.category} onValueChange={(v) => set('category', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['fuel', 'oil', 'toll', 'mixed'].map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => set('is_active', e.target.checked)}
              className="h-4 w-4 rounded border-input"
            />
            Active
          </label>

          {errorMsg && <p className="text-sm text-destructive">{errorMsg}</p>}
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={!canSubmit || isPending}>
            {isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create merchant'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  return (
    <label className="grid gap-1 text-sm">
      <span className="font-medium">
        {label}
        {required ? <span className="text-destructive"> *</span> : null}
      </span>
      {children}
    </label>
  );
}
